import React, { useMemo } from 'react';
import { Box, Tooltip, Chip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { WordScore, WordAnalysisData } from '@services/reading/types';

const HighlightedWord = styled('span')<{ qualityScore: number }>(({ theme, qualityScore }) => {
    // Color mapping based on quality score
    const getBackgroundColor = () => {
        if (qualityScore >= 80) return theme.palette.success.light;
        if (qualityScore >= 60) return theme.palette.warning.light;
        if (qualityScore >= 40) return theme.palette.error.light;
        return theme.palette.error.main;
    };

    const getTextColor = () => {
        if (qualityScore >= 80) return theme.palette.success.contrastText;
        if (qualityScore >= 60) return theme.palette.warning.contrastText;
        if (qualityScore >= 40) return theme.palette.error.contrastText;
        return theme.palette.error.contrastText;
    };

    return {
        backgroundColor: getBackgroundColor(),
        color: getTextColor(),
        padding: '2px 4px',
        margin: '0 1px',
        borderRadius: theme.shape.borderRadius,
        cursor: 'pointer',
        fontWeight: qualityScore < 60 ? 'bold' : 'normal',
        textDecoration: qualityScore < 40 ? 'underline' : 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: theme.shadows[2],
        },
    };
});

interface WordTooltipContentProps {
    wordData: WordAnalysisData;
}

const WordTooltipContent: React.FC<WordTooltipContentProps> = ({ wordData }) => {
    const { t } = useTranslation();

    return (
        <Box sx={{ p: 1, maxWidth: 300 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                "{wordData.word}" - {t('studentReading.wordHighlighting.score')} {wordData.quality_score}%
            </Typography>

            {/* Overall quality indicator */}
            <Box mb={1}>
                <Chip
                    label={
                        wordData.quality_score >= 80
                            ? t('studentReading.speechAnalysis.qualityLabels.excellent')
                            : wordData.quality_score >= 60
                            ? t('studentReading.speechAnalysis.qualityLabels.good')
                            : wordData.quality_score >= 40
                            ? t('studentReading.speechAnalysis.qualityLabels.needsPractice')
                            : t('studentReading.speechAnalysis.qualityLabels.requiresAttention')
                    }
                    color={
                        wordData.quality_score >= 80 ? 'success' : wordData.quality_score >= 60 ? 'warning' : 'error'
                    }
                    size="small"
                />
            </Box>

            {/* Phoneme details */}
            {wordData.phoneme_details?.length > 0 && (
                <Box mb={1}>
                    <Typography variant="caption" display="block" fontWeight="bold" gutterBottom>
                        {t('studentReading.wordHighlighting.phonemeAnalysis')}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {wordData.phoneme_details.map((phone, index) => (
                            <Chip
                                key={index}
                                label={`${phone.phone}: ${phone.quality_score}%`}
                                size="small"
                                variant="outlined"
                                color={
                                    phone.quality_score >= 70
                                        ? 'success'
                                        : phone.quality_score >= 50
                                        ? 'warning'
                                        : 'error'
                                }
                                sx={{ fontSize: '0.7rem' }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Syllable details */}
            {wordData.syllable_details?.length > 0 && (
                <Box mb={1}>
                    <Typography variant="caption" display="block" fontWeight="bold" gutterBottom>
                        {t('studentReading.wordHighlighting.syllableAnalysis')}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {wordData.syllable_details.map((syllable, index) => (
                            <Chip
                                key={index}
                                label={`${syllable.letters}: ${syllable.quality_score}%`}
                                size="small"
                                variant="outlined"
                                color={
                                    syllable.quality_score >= 70
                                        ? 'success'
                                        : syllable.quality_score >= 50
                                        ? 'warning'
                                        : 'error'
                                }
                                sx={{ fontSize: '0.7rem' }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Improvement suggestions */}
            {wordData.quality_score < 70 && (
                <Box>
                    <Typography variant="caption" display="block" fontWeight="bold" gutterBottom>
                        {t('studentReading.wordHighlighting.suggestions')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {wordData.quality_score < 40
                            ? t('studentReading.speechAnalysis.suggestions.phonemes')
                            : wordData.quality_score < 60
                            ? t('studentReading.speechAnalysis.suggestions.syllables')
                            : t('studentReading.speechAnalysis.suggestions.fineTune')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

interface WordHighlightedTextProps {
    text: string;
    wordScores: WordScore[];
    onClick?: (word: string, score: number) => void;
    className?: string;
}

const WordHighlightedText: React.FC<WordHighlightedTextProps> = ({ text, wordScores, onClick, className }) => {
    const { t } = useTranslation();

    const wordAnalysisData = useMemo((): WordAnalysisData[] => {
        if (!wordScores.length) return [];

        const words = text.split(/\s+/);
        const analysisData: WordAnalysisData[] = [];

        let currentPosition = 0;

        words.forEach((word, index) => {
            // Find matching word score (case-insensitive, handle punctuation)
            const cleanWord = word.replace(/[^\w]/g, '').toLowerCase();
            const wordScore = wordScores.find((ws) => ws.word.toLowerCase().replace(/[^\w]/g, '') === cleanWord);

            if (wordScore) {
                const qualityScore = wordScore.quality_score || 0;

                // Generate color class based on score
                const colorClass =
                    qualityScore >= 80
                        ? 'word-excellent'
                        : qualityScore >= 60
                        ? 'word-good'
                        : qualityScore >= 40
                        ? 'word-needs-practice'
                        : 'word-requires-attention';

                // Generate tooltip content
                const tooltipContent = `
          ${t('studentReading.wordHighlighting.score')} ${qualityScore}%
          ${t('studentReading.wordHighlighting.phonemes')} ${wordScore.phone_score_list?.length || 0}
          ${t('studentReading.wordHighlighting.quality')} ${
                    qualityScore >= 80
                        ? t('studentReading.speechAnalysis.qualityLabels.excellent')
                        : qualityScore >= 60
                        ? t('studentReading.speechAnalysis.qualityLabels.good')
                        : qualityScore >= 40
                        ? t('studentReading.speechAnalysis.qualityLabels.needsPractice')
                        : t('studentReading.speechAnalysis.qualityLabels.requiresAttention')
                }
        `;

                analysisData.push({
                    word,
                    quality_score: qualityScore,
                    position_start: currentPosition,
                    position_end: currentPosition + word.length,
                    phoneme_details: wordScore.phone_score_list,
                    syllable_details: wordScore.syllable_score_list,
                    color_class: colorClass,
                    tooltip_content: tooltipContent,
                });
            } else {
                // Default for words without scores
                analysisData.push({
                    word,
                    quality_score: 100, // Assume perfect if no data
                    position_start: currentPosition,
                    position_end: currentPosition + word.length,
                    phoneme_details: [],
                    syllable_details: [],
                    color_class: 'word-no-data',
                    tooltip_content: t('studentReading.wordHighlighting.noData'),
                });
            }

            currentPosition += word.length + 1; // +1 for space
        });

        return analysisData;
    }, [text, wordScores, t]);

    const handleWordClick = (wordData: WordAnalysisData) => {
        if (onClick) {
            onClick(wordData.word, wordData.quality_score);
        }
    };

    return (
        <Box className={className} sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
            {wordAnalysisData.map((wordData, index) => (
                <React.Fragment key={index}>
                    <Tooltip
                        title={<WordTooltipContent wordData={wordData} />}
                        placement="top"
                        arrow
                        enterDelay={300}
                        leaveDelay={200}
                    >
                        <HighlightedWord
                            qualityScore={wordData.quality_score}
                            onClick={() => handleWordClick(wordData)}
                        >
                            {wordData.word}
                        </HighlightedWord>
                    </Tooltip>
                    {index < wordAnalysisData.length - 1 && ' '}
                </React.Fragment>
            ))}
        </Box>
    );
};

export default WordHighlightedText;
